import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import { accountManagementRoute } from '@/features/AccountManagement/pages/AccountManagement'
import { auditLogsRoute } from '@/features/AuditLogs/pages/AuditLogsPage/AuditLogsPage'
import { clusterConfigTableRoute } from '@/features/ClusterConfigs/pages/ClusterConfigTable/ClusterConfigTable'
import { clusterUpdateTableRoute } from '@/features/ClusterConfigs/pages/ClusterUpdateTable/ClusterUpdateTable'
import { hivesOverviewRoute } from '@/features/HivesOverview/pages/HivesOverviewHivesTable'
import { templateTableRoute } from '@/features/Templates/page/TemplateTable'
import { SUCCESS_TOAST_OPTION, toaster } from '@/hooks/useToast/toastUtils'
import {
  auditLogsRootRoute,
  clusterUpdatesRootRoute,
  clustersRootRoute,
  hivesOverviewRootRoute,
  releaseNotesRootRoute,
  templatesRootRoute,
} from '@/router/rootRoutes'
import { Link as ChakraLink, Icon, Tag } from '@chakra-ui/react'
import { Sidebar as SidebarLibrary } from '@hivemq/ui-library'
import { useOktaAuth } from '@okta/okta-react'
import {
  type AnyRoute,
  Link,
  useMatches,
  useNavigate,
} from '@tanstack/react-router'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type SidebarGroup = {
  title: string
  items: Array<SidebarItem | SidebarItemLink | SidebarItemCopy>
}

enum SidebarItemType {
  Route = 'route',
  Link = 'link',
  Copy = 'copy',
}

type SidebarItem = {
  type: SidebarItemType.Route
  title: string
  tag?: string
  colorScheme?: string
  isNotExternalLink?: boolean
  /*
   * These have to be functions otherwise you will run into a chicken egg problem as within the bundling they exist
   * But in the runtime they have to be defined first.
   * The workaround is to define them as functions, which which you target the route object in the runtime.
   */
  getRoute: () => AnyRoute
  getRootRoute: () => AnyRoute
}

type SidebarItemLink = {
  type: SidebarItemType.Link
  title: string
  url: string
}

type SidebarItemCopy = {
  type: SidebarItemType.Copy
  title: string
}

export function Sidebar() {
  const { t } = useTranslation(undefined, { useSuspense: false })
  const matches = useMatches()
  const navigate = useNavigate()
  const okta = useOktaAuth()

  const [hasCopied, setHasCopied] = useState(false)
  const { hasAdminRights } = useRBACPermissions()

  const onCopy = async () => {
    await navigator.clipboard.writeText(
      okta.authState?.accessToken?.accessToken ?? '',
    )
    setHasCopied(true)
    setTimeout(() => setHasCopied(false), 2000)
  }

  useEffect(() => {
    if (hasCopied) {
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: t('copiedBearerToken'),
      })
    }
  })

  // Build groups array conditionally based on user permissions
  const supportGroup: SidebarGroup = {
    title: t('groups.support'),
    items: [
      {
        type: SidebarItemType.Route,
        title: t('sidebar.accountManagement'),
        getRoute: () => accountManagementRoute,
        getRootRoute: () => accountManagementRoute,
      },
      {
        type: SidebarItemType.Route,
        title: t('sidebar.auditLogs'),
        getRoute: () => auditLogsRoute,
        getRootRoute: () => auditLogsRootRoute,
      },
      {
        type: SidebarItemType.Route,
        title: t('sidebar.runningHives'),
        getRoute: () => hivesOverviewRoute,
        getRootRoute: () => hivesOverviewRootRoute,
      },
    ],
  }

  const configGroup: SidebarGroup = {
    title: t('groups.clusterConfigs'),
    items: [
      {
        type: SidebarItemType.Route,
        title: t('sidebar.v3ClusterRevisions'),
        getRoute: () => clusterConfigTableRoute,
        getRootRoute: () => clustersRootRoute,
      },
      {
        type: SidebarItemType.Route,
        title: t('sidebar.hiveUpdates'),
        getRoute: () => clusterUpdateTableRoute,
        getRootRoute: () => clusterUpdatesRootRoute,
      },
      {
        type: SidebarItemType.Route,
        title: t('sidebar.templates'),
        getRoute: () => templateTableRoute,
        getRootRoute: () => templatesRootRoute,
      },
    ],
  }

  const helpGroup: SidebarGroup = {
    title: t('groups.help'),
    items: [
      {
        type: SidebarItemType.Route,
        title: t('sidebar.releaseNotes'),
        getRoute: () => releaseNotesRootRoute,
        getRootRoute: () => releaseNotesRootRoute,
      },
      {
        type: SidebarItemType.Link,
        title: t('sidebar.documentation'),
        url: 'https://docs.google.com/document/d/1z-snSivljxA-yn9F5eF5s7Ypi2MAmN7HR5IQU1oQh7Q/edit?tab=t.0',
      },
      { type: SidebarItemType.Copy, title: t('copyBearerToken') },
    ],
  }

  // Only show Configuration section for users with admin rights
  // Safely call hasAdminRights with fallback for initial render
  const shouldShowConfig = hasAdminRights()
  const groups: SidebarGroup[] = [
    supportGroup,
    ...(shouldShowConfig && configGroup ? [configGroup] : []),
    helpGroup,
  ].filter(Boolean) // Filter out any undefined groups

  return (
    <SidebarLibrary.Root>
      {groups.map((group) => (
        <SidebarLibrary.Group Title={group.title} key={group.title}>
          <SidebarLibrary.List>
            {group.items.map((item) => (
              <Fragment key={item.title}>
                {item.type === SidebarItemType.Link && (
                  <SidebarLibrary.ListItem key={item.url}>
                    <ChakraLink
                      href={item.url}
                      target="blank"
                      rel="noreferrer noopener"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      textDecoration="none"
                      color="inherit"
                      _hover={{
                        backgroundColor: 'gray.100',
                      }}
                    >
                      {item.title}
                      <Icon
                        display="flex"
                        as={ExternalLink}
                        w={4}
                        h={4}
                        mr={2}
                      />
                    </ChakraLink>
                  </SidebarLibrary.ListItem>
                )}
                {item.type === SidebarItemType.Route && (
                  <SidebarLibrary.ListItem
                    key={item.getRoute().id}
                    isActive={matches.some((matchRoute) => {
                      return matchRoute.routeId === item.getRootRoute().id
                    })}
                  >
                    <Link
                      to={item.getRoute().to}
                      onClick={() => {
                        navigate(item.getRoute())
                      }}
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        display: 'flex',
                        width: '100%',
                      }}
                    >
                      {item.title}

                      {item?.tag && (
                        <Tag.Root
                          size="sm"
                          colorPalette={item.colorScheme}
                          mr={2}
                        >
                          <Tag.Label>{item.tag}</Tag.Label>
                        </Tag.Root>
                      )}
                    </Link>
                  </SidebarLibrary.ListItem>
                )}
                {item.type === SidebarItemType.Copy && (
                  <SidebarLibrary.ListItem
                    as="button"
                    aria-label={
                      hasCopied
                        ? t('copiedToClipboard') || 'Copied to clipboard'
                        : t('copyBearerToken') || 'Copy Bearer Token'
                    }
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    key={item.title}
                    onClick={onCopy}
                    pr={1}
                  >
                    {item.title}
                    <Icon
                      as={hasCopied ? Check : Copy}
                      mr={2}
                      p={1}
                      _hover={{
                        cursor: 'pointer',
                      }}
                    />
                  </SidebarLibrary.ListItem>
                )}
              </Fragment>
            ))}
          </SidebarLibrary.List>
        </SidebarLibrary.Group>
      ))}
    </SidebarLibrary.Root>
  )
}
